import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query} from "lit/decorators.js";
import {resolve} from "../../container";
import {KEYS, prepareEvents} from "../../common/utils/events";
import {fresh, ManagingResources} from "../../common/lifetime";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";
import {UnLitElement} from "../../common/elements";
import type {EopPopover} from "./popover/popover";
import {PageFeatures} from "./pageFeatures";

@customElement("eop-i-button")
export class EopIButton extends ManagingResources(UnLitElement) {
    @query(".i-button")
    public iButtonElement: HTMLButtonElement;
    @property({attribute: "i-button-style"})
    public iButtonStyle: string;
    @property()
    public label: string;
    @property({attribute: "popover-id"})
    private popoverId: string;

    public constructor(
        private pageFeatures: PageFeatures = resolve(PageFeatures)) {
        super();
    }

    /**
     * type button is used to prevent form submission when the button is inside a form
     */
    public render(): TemplateResult {
        return html`
            <div class="i-button-container">
                ${this.label}
                <button
                        type="button"
                        class="i-button ${this.iButtonStyle}"
                        aria-label="i-button"
                        ${trackingButtonInteract("i-button-toggle")}
                ></button>
            </div>
        `;
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        const lifetime = fresh(this, "firstUpdated");
        prepareEvents(this.iButtonElement)
            .boundTo(lifetime)
            .on("click", () => {
                this.pageFeaturePopover()?.toggle(this.iButtonElement);
            });
        prepareEvents(this.iButtonElement)
            .boundTo(lifetime)
            .on("keydown", (event: Event) => {
                const keyEvent = event as KeyboardEvent;
                if (keyEvent.key === KEYS.ESCAPE) {
                    this.pageFeaturePopover()?.closePopover();
                    this.iButtonElement.focus();
                }
            });

    }

    private pageFeaturePopover(): EopPopover | null {
        return this.pageFeatures.findFirst(`eop-popover[popover-id="${this.popoverId}"]`);
    }
}